import { Sport, useGetAllTeamsQuery, useGetPlays, useGetSport, useGetUser } from "../redux/slices";
import { BasketballCourtView, HockeyRinkView, SoccerPitchView, SportPlay } from "../types";

const RECENT_PLAY_DATE_CUTOFF = 1000 * 60 * 60 * 24; // 1 day
const DEFAULT_PLAY_NAME = "Untitled";
const DEFAULT_SEASON = "22-23";
const DEFAULT_BASKETBALL_COURT_TYPE = "NCAA"; // should this come from BE?
const DEFAULT_HOCKEY_COURT_TYPE = "NHL";
const DEFAULT_SOCCER_FIELD_TYPE = "NCAAS-M-D1";

export const useDefaultPlayData = () => {
  const { data: pagedData } = useGetPlays({
    fieldSearch: [],
    sort: { field: "createdTimestamp", order: "DESC" },
  });

  const plays = pagedData?.data;

  const { data: teams } = useGetAllTeamsQuery();
  const sport = useGetSport();
  const { user } = useGetUser();

  const getDefaultPlayData = (): SportPlay => {
    if (plays && plays[0]) {
      const mostRecentPlay = plays[0];
      // if the most recent play was created within the last day, use its tags
      const useRecentPlayTags = mostRecentPlay.createdTimestamp
        ? new Date(mostRecentPlay.createdTimestamp).valueOf() > Date.now() - RECENT_PLAY_DATE_CUTOFF
        : false;

      const playData: SportPlay = {
        ...mostRecentPlay,
        id: "",
        createdTimestamp: "",
        updatedTimestamp: "",
        tags: useRecentPlayTags ? mostRecentPlay.tags : [],
        frames: [],
        name: DEFAULT_PLAY_NAME,
        notes: "",
        orientation: 0,
      };

      return playData;
    } else {
      const userTeamId = user?.userDetails.teamId;

      const basePlayData: Omit<
        SportPlay,
        "playSurfaceId" | "playSurfaceView" | "courtOptions" | "sport" | "properties"
      > = {
        name: DEFAULT_PLAY_NAME,
        id: "",
        drawId: "",
        createdTimestamp: "",
        updatedTimestamp: "",
        frames: [],
        tags: [],
        season: DEFAULT_SEASON,
        team: teams?.find((team) => team.team_id === userTeamId),
        assets: [],
        recipients: [],
        orientation: 0,
      };

      return ((): SportPlay => {
        switch (sport) {
          case Sport.BASKETBALL:
            return {
              ...basePlayData,
              sport: Sport.BASKETBALL,
              courtOptions: {},
              playSurfaceId: DEFAULT_BASKETBALL_COURT_TYPE,
              playSurfaceView: BasketballCourtView.FRONTCOURT,
              properties: { outOfBoundsEnd: false, outOfBoundsLeft: false, outOfBoundsRight: false },
            };
          case Sport.HOCKEY:
            return {
              ...basePlayData,
              sport: Sport.HOCKEY,
              playSurfaceId: DEFAULT_HOCKEY_COURT_TYPE,
              playSurfaceView: HockeyRinkView.FULL,
              properties: {},
              courtOptions: {},
            };
          case Sport.SOCCER:
            return {
              ...basePlayData,
              sport: Sport.SOCCER,
              playSurfaceId: DEFAULT_SOCCER_FIELD_TYPE,
              playSurfaceView: SoccerPitchView.FULL,
              outOfBounds: true,
              properties: {},
              courtOptions: {},
            };

          case Sport.NO_SPORT:
            throw new Error(`Invalid sport: ${sport}`);
        }
      })();
    }
  };

  return { getDefaultPlayData };
};
